<script setup lang="ts">
  import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import { Filters } from '../ui';
  import HomeDefaultView from './HomeDefaultView.vue';
  import {
    useHomeSearch,
    useHomeNavigationReset,
    markVisibleDirectoryTreeStale,
    MODULE_MATRIX_REVISION_ROUTE_NAME,
    type DirectoryContentKind,
  } from '@/pages/home/model';
  import { useAuthStore } from '@/entities/Session';

  interface FiltersInstance {
    clearFilters: () => void;
  }

  interface NavigationItem {
    code?: string;
    name?: string;
    fullPath?: string[];
    metadata?: {
      folderId?: number;
      moduleId?: number;
      listable?: boolean;
      prefix?: string;
      contentKind?: DirectoryContentKind;
    };
  }

  const router = useRouter();
  const route = useRoute();
  const authStore = useAuthStore();

  // Marked on the way out, not on the way in: the next visit fetches the tree again, so a change made
  // in Diretórios or anywhere else shows up without a page reload, and a direct load of a listing
  // reuses the tree the route guard has just read.
  onUnmounted(markVisibleDirectoryTreeStale);

  const filtersRef = ref<FiltersInstance | null>(null);

  const { clearCache } = useHomeSearch();

  const { clearNavigationState } = useHomeNavigationReset(clearCache);

  onMounted(async () => {
    if (route.name === 'Home' || route.path === '/') {
      await nextTick();
      clearNavigationState(() => filtersRef.value?.clearFilters());
    }
  });

  function handleItemNavigate(item: NavigationItem) {
    if (item.metadata?.prefix) {
      router.push({ name: 'QualityRecordMatrix', params: { prefix: item.metadata.prefix } });
      return;
    }

    if (item.metadata?.contentKind === 'MATRIX_REVISION' && item.code) {
      router.push({ name: MODULE_MATRIX_REVISION_ROUTE_NAME, params: { folderCode: item.code } });
      return;
    }

    let routeParam: string;

    if ((item.metadata?.folderId || item.metadata?.listable) && item.code) {
      routeParam = item.code;
    } else if (item.fullPath) {
      routeParam = item.fullPath.join('-');
    } else {
      routeParam = item.code ?? item.name ?? '';
    }

    if (!routeParam) return;

    router.push({ name: 'FilteredList', params: { itemName: routeParam } });
  }

  const isInListagem = computed(
    () =>
      route.name === 'QualityRecordMatrix' ||
      route.name === MODULE_MATRIX_REVISION_ROUTE_NAME ||
      (route.name === 'FilteredList' && Boolean(route.params.itemName))
  );
</script>

<template>
  <div class="h-full flex">
    <div class="w-1/5 shrink-0">
      <Filters
        ref="filtersRef"
        :is-authenticated="authStore.isAuthenticated"
        @item-navigate="handleItemNavigate"
      />
    </div>

    <div class="flex-1 overflow-auto">
      <router-view v-if="isInListagem" />
      <HomeDefaultView v-else />
    </div>
  </div>
</template>
